import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';

const STATUS_STYLES = {
  AWAITING_FUNDS: { bg: 'bg-status-yellow-bg', text: 'text-status-yellow', label: 'Awaiting Payment' },
  FUNDS_RECEIVED: { bg: 'bg-status-blue-bg', text: 'text-status-blue', label: 'Payment Confirmed' },
  PROCESSING:     { bg: 'bg-status-purple-bg', text: 'text-status-purple', label: 'Processing Transfer' },
  SENT:           { bg: 'bg-status-blue-bg', text: 'text-status-blue', label: 'Transfer in Progress' },
  COMPLETED:      { bg: 'bg-status-green-bg', text: 'text-status-green', label: 'Completed' },
  FAILED:         { bg: 'bg-status-red-bg', text: 'text-status-red', label: 'Failed' },
};

const ALL_STATUSES = ['ALL', 'AWAITING_FUNDS', 'FUNDS_RECEIVED', 'PROCESSING', 'SENT', 'COMPLETED', 'FAILED'];

const formatCurrency = (amount, currency) => {
  if (amount == null) return '—';
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(n)) return '—';
  const fmt = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (currency === 'NGN') return '₦' + fmt;
  if (currency === 'CFA' || currency === 'XOF') return 'CFA ' + fmt;
  return fmt;
};

const formatDate = (d) => {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function Transactions({ merchantData }) {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');
      const params = { action: 'getMerchantTransactions', limit: 100 };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const result = await merchantApi(params);
      setTransactions(result.data.transactions || []);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  // Client-side search filter
  const filtered = transactions.filter(tx => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      tx.reference?.toLowerCase().includes(q) ||
      tx.recipientName?.toLowerCase().includes(q) ||
      tx.recipientBank?.toLowerCase().includes(q) ||
      tx.recipientAccount?.toLowerCase().includes(q) ||
      tx.recipientPhone?.toLowerCase().includes(q) ||
      tx.id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-text-main">Transactions</h1>
        <button
          onClick={fetchTransactions}
          disabled={loading}
          className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-light transition-colors"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} size={16} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by reference, recipient..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {ALL_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                statusFilter === s
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
              }`}
            >
              {s === 'ALL' ? 'All' : (STATUS_STYLES[s]?.label || s)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-xl shadow-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-text-secondary text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-text-secondary text-sm">No transactions found</div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Reference</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Send</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Receive</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Recipient</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Bank/Network</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Account/Phone</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Status</th>
                    <th className="text-left p-4 text-xs font-medium text-text-secondary">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((tx) => {
                    const style = STATUS_STYLES[tx.status] || {};
                    return (
                      <tr
                        key={tx.id}
                        onClick={() => navigate(`/transactions/${tx.id}`)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="p-4 font-mono text-xs text-primary">{tx.reference}</td>
                        <td className="p-4 font-medium">{formatCurrency(tx.amount, tx.inputCurrency)}</td>
                        <td className="p-4 font-medium">{formatCurrency(tx.outputAmount, tx.outputCurrency)}</td>
                        <td className="p-4 text-text-secondary">{tx.recipientName || '—'}</td>
                        <td className="p-4 text-text-secondary">{tx.recipientBank || '—'}</td>
                        <td className="p-4 text-text-secondary">{tx.recipientAccount || tx.recipientPhone || '—'}</td>
                        <td className="p-4">
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${style.bg || 'bg-gray-100'} ${style.text || 'text-gray-600'}`}>
                            {style.label || tx.status}
                          </span>
                        </td>
                        <td className="p-4 text-text-secondary text-xs">{formatDate(tx.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-50">
              {filtered.map((tx) => {
                const style = STATUS_STYLES[tx.status] || {};
                return (
                  <div
                    key={tx.id}
                    onClick={() => navigate(`/transactions/${tx.id}`)}
                    className="p-4 active:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-xs text-primary truncate mr-2">{tx.reference}</span>
                      <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${style.bg || ''} ${style.text || ''} whitespace-nowrap`}>
                        {style.label || tx.status}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-text-main">
                      {formatCurrency(tx.amount, tx.inputCurrency)} → {formatCurrency(tx.outputAmount, tx.outputCurrency)}
                    </p>
                    <p className="text-xs text-text-secondary mt-1 truncate">
                      {tx.recipientName} · {tx.recipientBank} · {tx.recipientAccount || tx.recipientPhone} · {formatDate(tx.createdAt)}
                    </p>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
